import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  ArrowUpward,
  ArrowDownward,
  CheckCircle
} from '@mui/icons-material';
import axios from 'axios';
import { useToast } from '../../../contexts/ToastContext';
import SignatureField from '../../camo/SignatureField';

/**
 * Component for managing Verification Checks in work orders
 */
const VerificationChecksManager = ({ workOrderId, items, onUpdate, user }) => {
  const [localItems, setLocalItems] = useState([]);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [completeDialogOpen, setCompleteDialogOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [completingIndex, setCompletingIndex] = useState(null);
  const [editFormData, setEditFormData] = useState({
    task: '',
    ameAuthSignature: null,
    ameAuthSignatureType: 'NAME'
  });
  const [loading, setLoading] = useState(false);
  const { showToast } = useToast();

  useEffect(() => {
    if (items) {
      setLocalItems(items);
    } else {
      setLocalItems([]);
    }
  }, [items]);

  const handleAdd = () => {
    setEditingIndex(null);
    setEditFormData({
      task: '',
      ameAuthSignature: null,
      ameAuthSignatureType: 'NAME'
    });
    setEditDialogOpen(true);
  };

  const handleEdit = (index) => {
    setEditingIndex(index);
    const item = localItems[index];
    setEditFormData({
      task: item.task || '',
      ameAuthSignature: item.ameAuthSignature?.signatureValue || null,
      ameAuthSignatureType: item.ameAuthSignature?.signatureType || 'NAME'
    });
    setEditDialogOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!editFormData.task.trim()) {
      showToast('Task description is required', 'error');
      return;
    }

    setLoading(true);
    try {
      if (editingIndex !== null) {
        const item = localItems[editingIndex];
        await axios.put(`/api/maintenance/${workOrderId}/verification-checks/${item._id}`, {
          task: editFormData.task
        });
      } else {
        await axios.post(`/api/maintenance/${workOrderId}/verification-checks`, {
          task: editFormData.task
        });
      }
      
      onUpdate();
      setEditDialogOpen(false);
      setEditingIndex(null);
      showToast(editingIndex !== null ? 'Check updated successfully' : 'Check added successfully', 'success');
    } catch (error) {
      console.error('Error saving check:', error);
      showToast(error.response?.data?.message || 'Failed to save check', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleComplete = (index) => {
    setCompletingIndex(index);
    const item = localItems[index];
    setEditFormData({
      task: item.task || '',
      ameAuthSignature: item.ameAuthSignature?.signatureValue || null,
      ameAuthSignatureType: item.ameAuthSignature?.signatureType || 'NAME'
    });
    setCompleteDialogOpen(true);
  };

  const handleSaveComplete = async () => {
    if (!editFormData.ameAuthSignature) {
      showToast('AME/AUTH Person\'s Signature is required', 'error');
      return;
    }

    setLoading(true);
    try {
      const item = localItems[completingIndex];
      await axios.put(`/api/maintenance/${workOrderId}/verification-checks/${item._id}/complete`, {
        ameAuthSignature: {
          signatureType: editFormData.ameAuthSignatureType,
          signatureValue: editFormData.ameAuthSignature,
          signedBy: user?._id,
          signedAt: new Date()
        }
      });
      
      onUpdate();
      setCompleteDialogOpen(false);
      setCompletingIndex(null);
      showToast('Verification check completed successfully', 'success');
    } catch (error) {
      console.error('Error completing check:', error);
      showToast(error.response?.data?.message || 'Failed to complete check', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (index) => {
    const item = localItems[index];
    try {
      await axios.delete(`/api/maintenance/${workOrderId}/verification-checks/${item._id}`);
      onUpdate();
      showToast('Check deleted successfully', 'success');
    } catch (error) {
      console.error('Error deleting check:', error);
      showToast(error.response?.data?.message || 'Failed to delete check', 'error');
    }
  };

  const handleMoveUp = async (index) => {
    if (index === 0) return;
    try {
      const updated = [...localItems];
      [updated[index - 1], updated[index]] = [updated[index], updated[index - 1]];
      const reordered = updated.map((item, i) => ({
        ...item,
        sequenceNumber: i + 1
      }));
      await axios.put(`/api/maintenance/${workOrderId}/verification-checks/reorder`, {
        checks: reordered
      });
      onUpdate();
    } catch (error) {
      console.error('Error reordering checks:', error);
      showToast('Failed to reorder checks', 'error');
    }
  };

  const handleMoveDown = async (index) => {
    if (index === localItems.length - 1) return;
    try {
      const updated = [...localItems];
      [updated[index], updated[index + 1]] = [updated[index + 1], updated[index]];
      const reordered = updated.map((item, i) => ({
        ...item,
        sequenceNumber: i + 1
      }));
      await axios.put(`/api/maintenance/${workOrderId}/verification-checks/reorder`, {
        checks: reordered
      });
      onUpdate();
    } catch (error) {
      console.error('Error reordering checks:', error);
      showToast('Failed to reorder checks', 'error');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'COMPLETED':
        return 'success';
      case 'IN_PROGRESS':
        return 'warning';
      default:
        return 'default';
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Verification Checks</Typography>
        <Button
          variant="outlined"
          size="small"
          startIcon={<Add />}
          onClick={handleAdd}
        >
          Add Check
        </Button>
      </Box>

      {localItems.length === 0 ? (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            No verification checks added. Click "Add Check" to add one.
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell width="80">S/N</TableCell>
                <TableCell>Task</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Completed By</TableCell>
                <TableCell>Completed At</TableCell>
                <TableCell width="200">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {localItems.map((item, index) => (
                <TableRow key={item._id || index}>
                  <TableCell>{item.sequenceNumber}</TableCell>
                  <TableCell>{item.task}</TableCell>
                  <TableCell>
                    <Chip
                      label={item.status || 'PENDING'}
                      color={getStatusColor(item.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {item.completedBy?.firstName && item.completedBy?.lastName
                      ? `${item.completedBy.firstName} ${item.completedBy.lastName}`
                      : '-'}
                  </TableCell>
                  <TableCell>
                    {item.completedAt ? new Date(item.completedAt).toLocaleString() : '-'}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <IconButton
                        size="small"
                        onClick={() => handleMoveUp(index)}
                        disabled={index === 0}
                      >
                        <ArrowUpward fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleMoveDown(index)}
                        disabled={index === localItems.length - 1}
                      >
                        <ArrowDownward fontSize="small" />
                      </IconButton>
                      {item.status !== 'COMPLETED' && (
                        <>
                          <IconButton
                            size="small"
                            onClick={() => handleEdit(index)}
                          >
                            <Edit fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleComplete(index)}
                          >
                            <CheckCircle fontSize="small" />
                          </IconButton>
                        </>
                      )}
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDelete(index)}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingIndex !== null ? 'Edit Verification Check' : 'Add Verification Check'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              fullWidth
              label="Task *"
              value={editFormData.task}
              onChange={(e) => setEditFormData({ ...editFormData, task: e.target.value })}
              required
              multiline
              rows={3}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)} disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={handleSaveEdit}
            variant="contained"
            disabled={loading || !editFormData.task.trim()}
          >
            {loading ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Complete Dialog */}
      <Dialog open={completeDialogOpen} onClose={() => setCompleteDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Complete Verification Check</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Typography variant="body1" gutterBottom>
              <strong>Task:</strong> {editFormData.task}
            </Typography>
            <SignatureField
              label="AME/AUTH Person's Signature *"
              value={editFormData.ameAuthSignature}
              signatureType={editFormData.ameAuthSignatureType}
              onChange={(value, type) => setEditFormData({
                ...editFormData,
                ameAuthSignature: value,
                ameAuthSignatureType: type
              })}
              entityId={workOrderId}
              entityType="VERIFICATION_CHECK_SIGNATURE"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCompleteDialogOpen(false)} disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={handleSaveComplete}
            variant="contained"
            disabled={loading || !editFormData.ameAuthSignature}
          >
            {loading ? 'Completing...' : 'Complete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default VerificationChecksManager;
